import * as React from "react";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import RefreshIcon from "@material-ui/icons/Refresh";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigPost, getConfigGet } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    padding: "3em 2em",
    boxSizing: "border-box",
    width: "100%",
    overflow: "hidden",
  },
  container: {
    width: "100%",
    maxWidth: "700px",
    boxSizing: "border-box",
    padding: "0 1em",
  },
  pageTitle: {
    marginBottom: "2em",
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "2rem",
  },
  card: {
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    borderRadius: "12px",
    marginBottom: "2em",
  },
  formField: {
    marginBottom: "1.5em",
    "& .MuiInputBase-root": {
      backgroundColor: "white",
    },
  },
  submitButton: {
    padding: "12px 48px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(63, 81, 181, 0.3)",
    transition: "all 0.3s ease",
    marginTop: "1em",
    "&:hover": {
      boxShadow: "0 6px 16px rgba(63, 81, 181, 0.4)",
      transform: "translateY(-2px)",
    },
  },
  warningBox: {
    marginBottom: "2em",
    padding: "1.5em",
    backgroundColor: "#fff3e0",
    borderRadius: "8px",
    borderLeft: "4px solid #e65100",
  },
  warningText: {
    color: "#e65100",
    fontSize: "0.95rem",
  },
  resultCard: {
    marginTop: "2em",
    padding: "2em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
    color: "white",
  },
  resultTitle: {
    color: "rgba(255,255,255,0.9)",
    marginBottom: "1em",
    fontSize: "1.2rem",
    fontWeight: 600,
  },
  resultItem: {
    marginBottom: "1em",
    padding: "0.75em",
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: "8px",
  },
  resultLabel: {
    color: "rgba(255,255,255,0.9)",
    fontSize: "0.9rem",
    marginBottom: "0.25em",
  },
  resultValue: {
    color: "white",
    fontSize: "1.1rem",
    fontWeight: 600,
  },
  balanceCard: {
    marginBottom: "2em",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(79, 172, 254, 0.4)",
  },
  balanceCardContent: {
    padding: "2em !important",
    textAlign: "center",
    background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    borderRadius: "16px",
    color: "white",
  },
  balanceIcon: {
    fontSize: "2.5rem",
    marginBottom: "0.5em",
  },
  balanceLabel: {
    fontSize: "1rem",
    opacity: 0.9,
    marginBottom: "0.5em",
  },
  balanceValue: {
    fontSize: "2rem",
    fontWeight: 700,
    marginBottom: "0.25em",
  },
  balanceSubtext: {
    fontSize: "0.9rem",
    opacity: 0.85,
  },
  refreshButton: {
    marginTop: "1em",
    color: "white",
    borderColor: "rgba(255,255,255,0.6)",
  },
}));

const ResultField = ({ classes, label, value }: { classes: any; label: string; value: any }) => (
  <div className={classes.resultItem}>
    <Typography className={classes.resultLabel}>{label}</Typography>
    <Typography className={classes.resultValue}>{value ?? "N/A"}</Typography>
  </div>
);

interface NeocurrencyBalance {
  neocurrency_balance: number;
  currency: string;
}

interface NeoCurrencyStatusResponse {
  success: boolean;
  payment_reference: string;
  transfer_status: string;
  neocurrency_topup_status: string;
  amount: number;
  created_at: string;
  updated_at: string;
}

const formatDate = (d: string | null | undefined): string => {
  if (!d) return "N/A";
  try {
    const date = new Date(d);
    return isNaN(date.getTime()) ? "N/A" : date.toLocaleString();
  } catch {
    return "N/A";
  }
};

const NeoCurrencyTopupDashboard = () => {
  const classes = useStyles();
  const notify = useNotify();

  // Balance
  const [neocurrencyBalance, setNeocurrencyBalance] = useState<NeocurrencyBalance | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Transfer
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Status check
  const [statusRef, setStatusRef] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusResult, setStatusResult] = useState<NeoCurrencyStatusResponse | null>(null);

  const fetchNeocurrencyBalance = () => {
    setLoadingBalance(true);
    const config = getConfigGet({ action: "get_neocurrency_balance" });

    axios
      .get(ENDPOINTS.PPS_MONEY_MANAGEMENT, config)
      .then((res: any) => {
        setLoadingBalance(false);
        setNeocurrencyBalance(res.data);
      })
      .catch((error) => {
        setLoadingBalance(false);
        notify(
          error?.response?.data?.message || "Failed to fetch NeoCurrency balance",
          "error"
        );
      });
  };

  useEffect(() => {
    fetchNeocurrencyBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!statusRef.trim()) { notify("Payment reference is required", "error"); return; }
    try {
      setStatusLoading(true);
      setStatusResult(null);
      const url = `${ENDPOINTS.NATWEST_TRANSFER}?flag=neocurrency_status&payment_reference=${encodeURIComponent(statusRef.trim())}`;
      const res = await axios.get<{ data: NeoCurrencyStatusResponse }>(url, { headers: getConfigPost() });
      const data = res.data.data || (res.data as unknown as NeoCurrencyStatusResponse);
      setStatusResult(data);
      notify("NeoCurrency status fetched", "info");
    } catch (err: any) {
      notify(err.response?.data?.message || "Failed to fetch NeoCurrency status", "error");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSend = async () => {
    setConfirmOpen(false);
    try {
      setLoading(true);
      const res = await axios.post<{ data: any }>(
        ENDPOINTS.NATWEST_TRANSFER,
        { flag: "neocurrency_topup", amount: Number(amount) },
        { headers: getConfigPost() }
      );
      const data = res.data.data || res.data;
      setResult(data);
      notify(`NeoCurrency top-up initiated: £${amount}`, "info");
    } catch (err: any) {
      notify(err.response?.data?.message || "NeoCurrency top-up failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.main}>
      <div className={classes.container}>
        <Typography variant="h4" className={classes.pageTitle}>
          NeoCurrency Top-Up
        </Typography>

        <Card className={classes.balanceCard}>
          <CardContent className={classes.balanceCardContent}>
            <AccountBalanceWalletIcon className={classes.balanceIcon} />
            <Typography className={classes.balanceLabel}>
              NeoCurrency Balance
            </Typography>
            {loadingBalance ? (
              <CircularProgress size={24} style={{ color: "white" }} />
            ) : neocurrencyBalance ? (
              <>
                <Typography className={classes.balanceValue}>
                  £{parseFloat(String(neocurrencyBalance.neocurrency_balance)).toFixed(2)}
                </Typography>
                <Typography className={classes.balanceSubtext}>
                  Currency: {neocurrencyBalance.currency}
                </Typography>
              </>
            ) : (
              <Typography className={classes.balanceSubtext}>
                Unable to load
              </Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              className={classes.refreshButton}
              onClick={fetchNeocurrencyBalance}
              disabled={loadingBalance}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </CardContent>
        </Card>

        <Card className={classes.card}>
          <CardContent>
            <div className={classes.warningBox}>
              <Typography className={classes.warningText}>
                ⚠ ADMIN + TECH only. It will transfer funds from NatWest to the NeoCurrency account via FPS. Amount in GBP.
              </Typography>
            </div>

            <TextField
              label="Amount (£)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              variant="outlined"
              className={classes.formField}
              type="number"
              inputProps={{ min: "1", step: "1" }}
              placeholder="1000"
              disabled={loading}
            />

            <Button
              variant="contained"
              color="primary"
              className={classes.submitButton}
              disabled={loading || !amount || Number(amount) <= 0}
              onClick={() => setConfirmOpen(true)}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Sending..." : "Send to NeoCurrency"}
            </Button>

            {result && (
              <Paper className={classes.resultCard}>
                <Typography className={classes.resultTitle}>✓ NeoCurrency Top-Up Initiated</Typography>
                <ResultField classes={classes} label="Payment Reference" value={result.payment_reference} />
                <ResultField classes={classes} label="Amount" value={`£${result.amount ?? amount}`} />
                <ResultField classes={classes} label="Transfer Status" value={result.transfer_status} />
                <ResultField classes={classes} label="NeoCurrency Top-Up Status" value={result.neocurrency_topup_status} />
              </Paper>
            )}
          </CardContent>
        </Card>

        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Check Transfer Status</Typography>
            <form onSubmit={handleStatusCheck}>
              <TextField
                label="Payment Reference"
                value={statusRef}
                onChange={(e) => setStatusRef(e.target.value)}
                fullWidth
                variant="outlined"
                className={classes.formField}
                placeholder="e.g. SPR15JUN261040"
                disabled={statusLoading}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submitButton}
                disabled={statusLoading || !statusRef.trim()}
                startIcon={statusLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {statusLoading ? "Loading..." : "Get Status"}
              </Button>
            </form>

            {statusResult && (
              <Paper className={classes.resultCard}>
                <Typography className={classes.resultTitle}>NeoCurrency Top-Up Status</Typography>
                <ResultField classes={classes} label="Payment Reference" value={statusResult.payment_reference} />
                <ResultField classes={classes} label="Amount" value={`£${statusResult.amount}`} />
                <ResultField classes={classes} label="Transfer Status" value={statusResult.transfer_status} />
                <ResultField classes={classes} label="NeoCurrency Top-Up Status" value={statusResult.neocurrency_topup_status} />
                <ResultField classes={classes} label="Created" value={formatDate(statusResult.created_at)} />
                <ResultField classes={classes} label="Updated" value={formatDate(statusResult.updated_at)} />
              </Paper>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm NeoCurrency Top-Up</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to transfer <strong>£{amount}</strong> from the Sprive NatWest account to NeoCurrency.
          </Typography>
          <Typography style={{ marginTop: "1em", color: "#c62828" }}>
            This will initiate a real bank transfer. Please confirm.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="default">Cancel</Button>
          <Button onClick={handleSend} color="primary" variant="contained">Confirm & Send</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NeoCurrencyTopupDashboard;
